import { useState, useEffect, useRef, useCallback } from "react";
import { useTheme } from "@/theme/theme-context";
import { useProduct } from "../product/product-context";
import { MarketChart } from "./market-chart";
import { ProductSelect } from "@/header/header-product-select";
import { TickerStats } from "@/header/header-ticker-stats";
import { Maximize2Icon, Minimize2Icon } from "lucide-react";
import { cn } from "@/lib/utils";
import { TradingPanelCard } from "@/features/shared/trading-panel-card";
import { MobileMarketHeader } from "@/components/mobile-market-header";

type MarketChartPanelProps = {
  panelId: string;
};

export const MarketChartPanel = ({ panelId: _panelId }: MarketChartPanelProps) => {
  const { product } = useProduct();
  const { theme } = useTheme();
  const [isMobile, setIsMobile] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1000);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Handle fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const toggleFullscreen = useCallback(async () => {
    if (!chartContainerRef.current) return;
    
    try {
      if (!document.fullscreenElement) {
        await chartContainerRef.current.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.error("Fullscreen error:", err);
    }
  }, []);

  if (!product) {
    return null;
  }

  return (
    <TradingPanelCard noPadding className="overflow-hidden">
      <div 
        ref={chartContainerRef}
        className={cn(
          "flex flex-col h-full",
          isFullscreen && "bg-background"
        )}
      >
        {/* Custom Header */}
        {isMobile ? (
          <MobileMarketHeader />
        ) : (
          <div className="flex items-center pl-5 pr-3 py-3 border-b-2 bg-card rounded-t-lg relative" style={{ borderBottomColor: theme === 'dark' ? 'oklch(0.10 0 0)' : 'oklch(0.95 0 0)' }}>
            <div data-tour="asset-selector">
              <ProductSelect />
            </div>

            <div className="ml-8 flex-1 min-w-0 overflow-hidden" data-tour="ticker-stats">
              <TickerStats />
            </div>

            <div className="ml-4 flex-shrink-0 flex items-center">
              {/* Fullscreen Button */}
              <button
                type="button"
                onClick={toggleFullscreen}
                className="cursor-pointer text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center p-1.5 rounded hover:bg-muted/50"
                title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
              >
                {isFullscreen ? (
                  <Minimize2Icon className="size-4" />
                ) : (
                  <Maximize2Icon className="size-4" />
                )}
              </button>
            </div>
          </div>
        )}

        {/* Chart Container */}
        <div className="flex-1 relative" data-tour="chart">
          <MarketChart theme={theme} productId={product.id} isMobile={isMobile} />
        </div>
      </div>
    </TradingPanelCard>
  );
};
